import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, ButtonSizes, ButtonVariants, Table } from '@carboledger/ui';
import { FileUp, Plus } from '@carboledger/ui/icons';
import { ROUTES } from '@/app/routes';
import { PageHeader } from '@/components/layout';
import { buildTableLabels } from '@/config/tableLabels';
import { useTableState, useTranslations } from '@/hooks';
import { EMPTY_LIST } from '@/lib/utils';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useGetCustomersListQuery } from '../api/customersApi';
import { CustomerFormModal } from '../components/CustomerFormModal';
import { ImportUnavailableModal } from '../components/ImportUnavailableModal';
import { TransportationFormModal } from '../components/TransportationFormModal';
import { buildCustomersColumns } from '../constants/customersColumns';
import styles from './CustomersListPage.module.scss';

const getCustomerRowId = (row) => row.id;

export const CustomersListPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const table = useTableState();

  const [editTarget, setEditTarget] = useState(null);
  const [transportCustomer, setTransportCustomer] = useState(null);
  const [isImportOpen, setIsImportOpen] = useState(false);

  const { data, isFetching } = useGetCustomersListQuery(table.queryParams);
  const rows = data?.rows ?? EMPTY_LIST;

  const handleOpenCustomer = useCallback(
    (row) => navigate(ROUTES.customerDetail(row.id)),
    [navigate],
  );

  const handleAdd = useCallback(() => setEditTarget({}), []);
  const handleEdit = useCallback((row) => setEditTarget({ customerId: row.id }), []);
  const handleCloseCustomerModal = useCallback(() => setEditTarget(null), []);

  const handleAddSites = useCallback(
    (row) => navigate(ROUTES.customerSitesCreate(row.id)),
    [navigate],
  );

  const handleAddTransportation = useCallback((row) => setTransportCustomer(row), []);
  const handleCloseTransportation = useCallback(() => setTransportCustomer(null), []);

  // TODO: wire ImportWizard once built
  const handleImport = useCallback(() => setIsImportOpen(true), []);
  const handleCloseImport = useCallback(() => setIsImportOpen(false), []);

  const handleSortChange = useCallback(
    (next) => {
      table.setSort(next ? { field: next.id, direction: next.dir } : null);
      table.setPage(1);
    },
    [table],
  );

  const handleSearchChange = useCallback(
    (value) => {
      table.setSearch(value);
      table.setPage(1);
    },
    [table],
  );

  const columns = useMemo(
    () =>
      buildCustomersColumns(t, {
        serialOffset: (table.page - 1) * table.pageSize,
        onOpenCustomer: handleOpenCustomer,
      }),
    [t, table.page, table.pageSize, handleOpenCustomer],
  );

  const rowMenuActions = useMemo(
    () => [
      { id: 'edit', label: t('COMMON_EDIT'), onClick: handleEdit },
      { id: 'add-sites', label: t('CUSTOMERS_ACTION_ADD_SITES'), onClick: handleAddSites },
      {
        id: 'add-transportation',
        label: t('CUSTOMERS_ACTION_ADD_TRANSPORTATION'),
        onClick: handleAddTransportation,
      },
    ],
    [t, handleEdit, handleAddSites, handleAddTransportation],
  );

  const addIcon = useMemo(() => <Plus size={16} />, []);
  const importIcon = useMemo(() => <FileUp size={16} />, []);

  const emptyState = useMemo(
    () => ({
      image: <img src={emptyBoxIllustration} alt="" width={200} height={200} />,
      title: t('CUSTOMERS_EMPTY_TITLE'),
      description: t('CUSTOMERS_EMPTY_DESCRIPTION'),
      primaryLabel: t('CUSTOMERS_ADD'),
      primaryIcon: addIcon,
      onPrimaryClick: handleAdd,
      secondaryLabel: t('CUSTOMERS_IMPORT'),
      secondaryIcon: importIcon,
      onSecondaryClick: handleImport,
    }),
    [t, addIcon, importIcon, handleAdd, handleImport],
  );

  const noResults = useMemo(
    () => ({
      title: t('CUSTOMERS_NO_RESULTS_TITLE'),
      description: t('COMMON_NO_RESULTS_DESCRIPTION'),
    }),
    [t],
  );

  const tableLabels = useMemo(() => buildTableLabels(t), [t]);

  const headerActions = useMemo(
    () => (
      <>
        <Button
          variant={ButtonVariants.secondary}
          size={ButtonSizes.sm}
          leftIcon={importIcon}
          onClick={handleImport}
        >
          {t('CUSTOMERS_IMPORT_SITES')}
        </Button>
        <Button
          variant={ButtonVariants.secondary}
          size={ButtonSizes.sm}
          leftIcon={importIcon}
          onClick={handleImport}
        >
          {t('CUSTOMERS_IMPORT')}
        </Button>
        <Button
          variant={ButtonVariants.primary}
          size={ButtonSizes.sm}
          leftIcon={addIcon}
          onClick={handleAdd}
        >
          {t('CUSTOMERS_ADD')}
        </Button>
      </>
    ),
    [t, importIcon, addIcon, handleImport, handleAdd],
  );

  return (
    <div className={styles.page}>
      <PageHeader title={t('HOME_MANAGE_CUSTOMERS')} actions={headerActions} />

      <Table
        columns={columns}
        data={rows}
        total={data?.totalCount ?? 0}
        loading={isFetching}
        getRowId={getCustomerRowId}
        searchable
        onSortChange={handleSortChange}
        onSearchChange={handleSearchChange}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
        rowMenuActions={rowMenuActions}
        actionsHeader={t('FIELD_ACTION')}
        emptyState={emptyState}
        noResults={noResults}
        labels={tableLabels}
      />

      <CustomerFormModal
        open={editTarget !== null}
        customerId={editTarget?.customerId}
        onClose={handleCloseCustomerModal}
      />

      <TransportationFormModal
        open={transportCustomer !== null}
        customerId={transportCustomer?.id}
        onClose={handleCloseTransportation}
      />

      <ImportUnavailableModal open={isImportOpen} onClose={handleCloseImport} />
    </div>
  );
};
